import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  CheckCircle, Package, Truck, MapPin, Mail, 
  ArrowRight, Loader2, AlertCircle, ShoppingBag
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CostumeOrderConfirmation() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyOrder();
    } else {
      setLoading(false);
      setError('No order session found');
    }
  }, [sessionId]);

  const verifyOrder = async () => {
    try {
      const response = await axios.get(`${API}/costumes/order/verify/${sessionId}`);
      
      if (response.data.status === 'paid') {
        setOrder(response.data.order);
      } else {
        setError('Payment is still processing. Please check back in a moment.');
      }
    } catch (err) {
      console.error('Error verifying order:', err);
      setError('Unable to verify your order. Please check your email for confirmation.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="confirmation-loading">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying your order...</p>
        </div>
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="confirmation-error">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Order Processing</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link
            to="/costume-shop"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all"
          >
            <ShoppingBag className="w-5 h-5" />
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6" data-testid="costume-order-confirmation">
      <div className="max-w-2xl mx-auto">
        {/* Success Animation */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="text-center mb-8"
        >
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping" />
            <CheckCircle className="w-20 h-20 text-green-500 relative" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl md:text-4xl font-bold font-display text-foreground mb-2">
            Order Confirmed!
          </h1>
          <p className="text-muted-foreground">
            Thank you for your purchase. We're preparing your order now.
          </p>
        </motion.div>

        {/* Order Details Card */}
        {order && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card border border-border rounded-2xl overflow-hidden mb-8"
          >
            {/* Order Header */}
            <div className="p-6 bg-stone-900/50 border-b border-border">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order Number</p>
                  <p className="text-lg font-bold font-mono text-primary" data-testid="order-number">
                    {order.order_number}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="text-lg font-bold text-foreground" data-testid="order-total">
                    ${order.total?.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>

            {/* Order Items */}
            <div className="p-6 border-b border-border">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                Items Ordered
              </h3>
              <div className="space-y-4">
                {order.items?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-16 h-20 bg-stone-800 rounded-lg flex items-center justify-center">
                      <Package className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-foreground">{item.costume_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {item.size !== 'One Size' && `Size: ${item.size}`}
                        {item.color && ` • Color: ${item.color}`}
                        {` • Qty: ${item.quantity}`}
                      </p>
                    </div>
                    <p className="font-semibold text-foreground">${item.subtotal?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Info */}
            <div className="p-6">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                Delivery Method
              </h3>
              <div className="flex items-start gap-3">
                {order.is_local_pickup ? (
                  <>
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <MapPin className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Local Pickup</p>
                      <p className="text-sm text-muted-foreground">
                        800 Race Street, New Orleans, LA 70130
                      </p>
                      <p className="text-sm text-green-400 mt-1">
                        Available for pickup - We'll contact you when ready
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-2 bg-primary/20 rounded-lg">
                      <Truck className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground capitalize">
                        {order.shipping_method?.replace('_', ' ')} Shipping
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Shipping cost: ${order.shipping_cost?.toFixed(2)}
                      </p>
                      {order.shipping_address && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {order.shipping_address.street_address}, {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Email Notice */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex items-start gap-3 p-4 bg-stone-900/50 border border-border/50 rounded-xl mb-8"
        >
          <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-foreground">Confirmation Email Sent</p>
            <p className="text-xs text-muted-foreground">
              A confirmation email with your order details has been sent to {order?.customer_email || 'your email address'}.
            </p>
          </div>
        </motion.div>

        {/* What's Next */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-card border border-border rounded-2xl p-6 mb-8"
        >
          <h3 className="font-bold text-foreground mb-4">What's Next?</h3>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                1
              </div>
              <div>
                <p className="font-semibold text-foreground">Order Processing</p>
                <p className="text-sm text-muted-foreground">
                  Our team will begin preparing your costume within 1-2 business days.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-stone-700 text-foreground flex items-center justify-center text-sm font-bold">
                2
              </div>
              <div>
                <p className="font-semibold text-foreground">Quality Check</p>
                <p className="text-sm text-muted-foreground">
                  Every costume is carefully inspected before shipping or pickup.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-stone-700 text-foreground flex items-center justify-center text-sm font-bold">
                3
              </div>
              <div>
                <p className="font-semibold text-foreground">
                  {order?.is_local_pickup ? 'Ready for Pickup' : 'Shipped'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {order?.is_local_pickup 
                    ? "We'll call you when your order is ready for pickup."
                    : "You'll receive tracking information via email once shipped."
                  }
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            to="/costume-shop"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-primary text-primary-foreground rounded-full font-semibold hover:bg-primary/90 transition-all"
            data-testid="continue-shopping-btn"
          >
            <ShoppingBag className="w-5 h-5" />
            Continue Shopping
          </Link>
          <Link
            to="/contact"
            className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-stone-800 text-foreground rounded-full font-semibold hover:bg-stone-700 transition-all"
          >
            Questions? Contact Us
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
